import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useCreateLabel, useUpdateLabel } from "@/hooks/use-labels";
import { colorPalette } from "@/lib/colors";
import { cn, extractErrorMessage } from "@/lib/utils";
import { LabelType } from "@/types/common";
import { ILabel } from "@/types/label.types";
import { zodResolver } from "@hookform/resolvers/zod";
import { Circle } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import * as z from "zod";
import { Badge } from "../../components/ui/badge";

const labelFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().min(1, "Description is required"),
  colorCode: z.string().regex(/^#/, "Must be a valid hex color"),
});

type LabelFormValues = z.infer<typeof labelFormSchema>;

interface AddLabelDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  editLabel?: ILabel;
  onSubmit?: (label: ILabel) => Promise<void>;
  labelType?: LabelType;
}

export function AddLabelDialog({
  open,
  onOpenChange,
  editLabel,
  labelType = LabelType.MAILBOX,
  onSubmit,
}: AddLabelDialogProps) {
  const form = useForm<LabelFormValues>({
    resolver: zodResolver(labelFormSchema),
    defaultValues: {
      name: "",
      description: "",
      colorCode: colorPalette[0],
    },
  });

  const createLabel = useCreateLabel();
  const updateLabel = useUpdateLabel();

  useEffect(() => {
    form.reset({
      name: editLabel?.name || "",
      description: editLabel?.description || "",
      colorCode: editLabel?.color || colorPalette[0],
    });
  }, [editLabel, form]);

  const handleSubmit = async (data: LabelFormValues) => {
    try {
      if (editLabel) {
        const updatedLabel = await updateLabel.mutateAsync({
          id: editLabel._id,
          labelData: {
            name: data.name,
            description: data.description,
            color: data.colorCode,
          },
        });
        toast.success("Label updated successfully");
        window.cioanalytics?.track("label_updated", {
          labelName: data.name,
          labelType: labelType,
        });
        onSubmit?.(updatedLabel);
      } else {
        const createdLabel = await createLabel.mutateAsync({
          name: data.name,
          description: data.description,
          color: data.colorCode,
          labelType: labelType,
        });
        toast.success("Label created successfully");
        window.cioanalytics?.track("label_created", {
          labelName: data.name,
          labelType: labelType,
        });
        onSubmit?.(createdLabel);
      }
      onOpenChange?.(false);
    } catch (error) {
      console.error("Error submitting label:", error);

      const baseErrorMessage = editLabel
        ? "Failed to update label"
        : "Failed to create label, label already exists";
      const errorMessage = extractErrorMessage(error, baseErrorMessage);

      toast.error(errorMessage);
    }
  };

  const currentLabelName = form.watch("name");

  const combinedLoading = createLabel.isPending || updateLabel.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>
            {editLabel ? "Edit Label" : "Add Email Label"}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="space-y-0.5">
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Add Label name"
                      disabled={combinedLoading}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem className="space-y-0.5">
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Add Label description"
                      className="min-h-[100px]"
                      disabled={combinedLoading}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="colorCode"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>Select color</FormLabel>
                  <FormControl>
                    <div className="space-y-3">
                      <div className="grid grid-cols-8 sm:grid-cols-10 gap-2">
                        {colorPalette.map((color) => (
                          <button
                            key={color}
                            type="button"
                            onClick={() => field.onChange(color)}
                            className={cn(
                              "h-6 w-6 rounded-sm transition-all hover:scale-110",
                              field.value === color &&
                                "ring-2 ring-primary ring-offset-2"
                            )}
                            style={{ backgroundColor: color }}
                            disabled={combinedLoading}
                          />
                        ))}
                      </div>
                      <div>
                        <p className="text-sm font-medium mb-1">Preview</p>
                        <div className="flex items-center justify-between gap-2">
                          {/* <div
                          className="h-6 w-20 rounded-sm"
                          style={{ backgroundColor: field.value }}
                        /> */}
                          <div className="flex items-center gap-2">
                            <Badge
                              className={cn(
                                "text-white shadow-none max-w-40"
                              )}
                              style={{
                                backgroundColor: field.value,
                              }}
                            >
                              <Circle className="h-2 w-2 fill-current shrink-0 mr-1" />
                              <span className="truncate">
                                {currentLabelName || "Label"}
                              </span>
                            </Badge>
                            <Badge
                              className={cn(
                                "text-black shadow-none max-w-40"
                              )}
                              style={{
                                backgroundColor: field.value,
                              }}
                            >
                              <Circle className="h-2 w-2 fill-current shrink-0 mr-1" />
                              <span className="truncate">
                                {currentLabelName || "Label"}
                              </span>
                            </Badge>
                          </div>
                          <span className="text-sm font-medium uppercase">
                            {field.value}
                          </span>
                        </div>
                      </div>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange?.(false)}
                disabled={combinedLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700"
                disabled={combinedLoading || !form.formState.isDirty}
              >
                {editLabel ? "Update Label" : "Add Label"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
